import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchPlayerProfile, type PlayerProfile } from '../api/players';
import { apiErrorCode } from '../api/client';
import type { Difficulty } from '../api/puzzles';
import { LetterTiles } from '../components/ui/LetterTiles';
import { Seo } from '../components/Seo';
import { TIER_COLORS, NEUTRAL_TIER_COLOR } from '../components/season/SeasonStrip';
import { MedalShelf } from '../components/season/MedalShelf';
import { backLabel, backTarget } from '../lib/back-origin';

const fmt = (n: number) => n.toLocaleString('en-GB');

const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  EASY: 'Easy',
  MEDIUM: 'Medium',
  HARD: 'Hard',
};

/** Tailwind classes, not inline hex — the brief pins these to the same
 *  utility classes the daily leaderboard's difficulty tabs already use
 *  (`emerald-800` / `blue-800` / `red-800`), not a separate palette. */
const DIFFICULTY_ACCENT: Record<Difficulty, string> = {
  EASY: 'bg-emerald-800',
  MEDIUM: 'bg-blue-800',
  HARD: 'bg-red-800',
};

/** 'YYYY-MM' → "August 2026". Month + year, never a finer timestamp — the
 *  API itself only carries month granularity for `playing_since`. */
function monthYearLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Circle-plus-ribbon medal glyph, matching `TierMedal` in SeasonStrip.tsx
 *  and `MedalGlyph` in MedalShelf.tsx — kept local since neither is
 *  exported and this task doesn't modify SeasonStrip.tsx. */
function MedalGlyph() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="9" r="6" />
      <path d="M8.5 14.5 7 22l5-2.5L17 22l-1.5-7.5" />
    </svg>
  );
}

/** The gold-barred (or whatever tier colour applies) season standing card —
 *  the current month's points/rank/tier, or a quiet "not ranked yet" state
 *  when the player hasn't played this season at all. */
function SeasonCard({ season }: { season: PlayerProfile['season'] }) {
  const tierColor = season?.tier ? TIER_COLORS[season.tier] : NEUTRAL_TIER_COLOR;

  return (
    <div
      data-testid="profile-season-card"
      className="overflow-hidden rounded-lg border border-tile-edge bg-[#fffdf6] shadow-[0_3px_0_rgba(217,199,154,0.9)]"
    >
      <div className="h-1.5" style={{ backgroundColor: tierColor }} aria-hidden="true" />
      <div className="flex items-center gap-3.5 px-4 py-3.5">
        <span
          aria-hidden="true"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#fffdf6]"
          style={{ backgroundColor: tierColor }}
        >
          <MedalGlyph />
        </span>
        {season ? (
          <div className="min-w-0 flex-1">
            <p data-testid="profile-season-tier" className="text-base font-bold text-stone-900">
              {season.tier ?? 'Unranked'} this season
            </p>
            <p className="mt-0.5 text-sm text-stone-600">
              <span data-testid="profile-season-points" className="tabular-nums">
                {fmt(season.points)}
              </span>{' '}
              pts
              {season.rank !== null ? (
                <>
                  {' '}
                  · rank #
                  <span data-testid="profile-season-rank" className="tabular-nums">
                    {fmt(season.rank)}
                  </span>
                </>
              ) : null}
            </p>
          </div>
        ) : (
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-stone-900">No standing yet this season</p>
            <p className="mt-0.5 text-sm text-stone-600">Hasn't finished a board this month.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function AllTimeStats({ allTime }: { allTime: PlayerProfile['all_time'] }) {
  const stats: Array<{ key: string; label: string; value: number }> = [
    { key: 'games', label: 'Games', value: allTime.games },
    { key: 'wins', label: 'Wins', value: allTime.wins },
    { key: 'best-streak', label: 'Best streak', value: allTime.best_streak },
  ];
  return (
    <div className="overflow-hidden rounded-lg border border-tile-edge bg-[#fffdf6] shadow-[0_3px_0_rgba(217,199,154,0.9)]">
      <p className="px-4 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-600">
        All time
      </p>
      <dl
        data-testid="profile-all-time"
        className="grid grid-cols-3 divide-x divide-tile-edge/60 border-t border-tile-edge/60"
      >
        {stats.map((s) => (
          <div key={s.key} className="px-2 py-3 text-center">
            <dd
              data-testid={`profile-all-time-${s.key}`}
              className="m-0 text-xl font-bold tabular-nums text-stone-900"
            >
              {fmt(s.value)}
            </dd>
            <dt className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-600">
              {s.label}
            </dt>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** 1st, 2nd, 3rd, 4th … — English ordinals, including the 11th/12th/13th
 *  exceptions that a naive "th unless it ends in 1/2/3" rule gets wrong. */
function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  const suffix = ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
  return `${n}${suffix}`;
}

function MonthRow({ entry }: { entry: PlayerProfile['this_month'][number] }) {
  const accent = DIFFICULTY_ACCENT[entry.difficulty];
  return (
    <div
      data-testid={`profile-month-row-${entry.difficulty}`}
      className="flex items-center gap-3 border-t border-tile-edge/60 px-4 py-2.5 first:border-t-0"
    >
      <span aria-hidden="true" className={`h-[26px] w-1 shrink-0 rounded-sm ${accent}`} />
      <span className="w-16 shrink-0 text-xs font-bold uppercase tracking-[0.1em] text-stone-900">
        {DIFFICULTY_LABEL[entry.difficulty]}
      </span>
      <span
        data-testid={`profile-month-best-${entry.difficulty}`}
        className="flex-1 text-sm font-bold tabular-nums text-stone-900"
      >
        {/* An em dash, not "0th": a board they have not finished this month
            has no placing at all, and a zero would read as a real result. */}
        {entry.best_position === null ? '—' : ordinal(entry.best_position)}
      </span>
      <span className="text-xs tabular-nums text-stone-600">
        {entry.won} of {entry.played} won
      </span>
    </div>
  );
}

function BoardRow({ entry }: { entry: PlayerProfile['by_difficulty'][number] }) {
  const pct = entry.played > 0 ? Math.round((entry.won / entry.played) * 100) : 0;
  const accent = DIFFICULTY_ACCENT[entry.difficulty];
  return (
    <div
      data-testid={`profile-board-row-${entry.difficulty}`}
      className="flex items-center gap-3 border-t border-tile-edge/60 px-4 py-2.5 first:border-t-0"
    >
      <span aria-hidden="true" className={`h-[26px] w-1 shrink-0 rounded-sm ${accent}`} />
      <span className="w-16 shrink-0 text-xs font-bold uppercase tracking-[0.1em] text-stone-900">
        {DIFFICULTY_LABEL[entry.difficulty]}
      </span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-board-bg">
        <div
          data-testid={`profile-board-fill-${entry.difficulty}`}
          className={`h-full rounded-full ${accent}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span
        data-testid={`profile-board-record-${entry.difficulty}`}
        className="w-16 shrink-0 text-right text-sm tabular-nums text-stone-600"
      >
        {entry.won} / {entry.played}
      </span>
    </div>
  );
}

function ProfileContent({ profile }: { profile: PlayerProfile }) {
  // Leaderboard names run to 24 characters — the generator's own worst case is
  // "<6-char adjective> <8-char animal> #999999" (23), and a custom name is
  // validated to 3-24. At `lg` that many tiles overflows the page sideways
  // even after wrapping, so step the tiles down once a name gets long. The
  // wordmark still reads as tiles; it just stops shouting.
  const nameSize = profile.leaderboard_name.length > 14 ? 'sm' : 'lg';

  return (
    <>
      <header className="text-center">
        <h1 className="leading-none">
          <LetterTiles
            word={profile.leaderboard_name}
            size={nameSize}
            label={`${profile.leaderboard_name}'s profile`}
          />
        </h1>
        <p className="mt-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-600">
          Playing since {monthYearLabel(profile.playing_since)}
        </p>
      </header>

      <div className="mt-5 flex flex-col gap-4">
        <SeasonCard season={profile.season} />
        <MedalShelf medals={profile.medals} />
        <AllTimeStats allTime={profile.all_time} />

        {/* This month only — best daily-leaderboard placing per board, with
            the boards won alongside. Kept as its own block rather than folded
            into "By board" below, which is all-time: one line mixing two
            timeframes would be unreadable. */}
        <div
          data-testid="profile-this-month"
          className="overflow-hidden rounded-lg border border-tile-edge bg-[#fffdf6] shadow-[0_3px_0_rgba(217,199,154,0.9)]"
        >
          <p className="px-4 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-600">
            Best finish this month
          </p>
          {profile.this_month.map((entry) => (
            <MonthRow key={entry.difficulty} entry={entry} />
          ))}
        </div>

        <div className="overflow-hidden rounded-lg border border-tile-edge bg-[#fffdf6] shadow-[0_3px_0_rgba(217,199,154,0.9)]">
          <p className="px-4 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-600">
            By board
          </p>
          {profile.by_difficulty.map((entry) => (
            <BoardRow key={entry.difficulty} entry={entry} />
          ))}
        </div>

        <p className="text-center text-xs text-stone-500">
          Today's boards stay hidden until the day closes.
        </p>
      </div>
    </>
  );
}

/** `/player/:name` — the public profile behind a leaderboard pseudonym.
 *  Fully public: no auth is read here, matching the server endpoint
 *  (`GET /api/players/:leaderboardName`), which never requires a session. */
export default function Profile() {
  const { name } = useParams<{ name: string }>();
  const location = useLocation();
  const decodedName = name ? decodeURIComponent(name) : '';
  const back = backTarget(location.state);

  const query = useQuery({
    queryKey: ['player', decodedName],
    queryFn: () => fetchPlayerProfile(decodedName),
    enabled: decodedName.length > 0,
    retry: false,
  });

  const notFound = query.isError && apiErrorCode(query.error) === 'PLAYER_NOT_FOUND';

  return (
    <section data-testid="page-profile">
      <Seo
        title={`${decodedName} — Scrabble Daily`}
        description={`${decodedName}'s season standing, medals and all-time stats on Scrabble Daily.`}
        path={`/player/${name ?? ''}`}
      />

      {/* Back to the exact page they came from — the hard board, or June's
          season — carried as router state by the link that got them here.
          Absent when the profile was opened cold (deep link, refresh, new
          tab), and nothing is rendered rather than guessing a destination
          that would drop them somewhere they have never been. */}
      {back ? (
        <p className="mb-1">
          <Link
            to={back}
            data-testid="profile-back-link"
            className="text-sm font-semibold text-emerald-800 hover:underline"
          >
            ← {backLabel(back)}
          </Link>
        </p>
      ) : null}

      {query.isLoading ? (
        <p role="status" className="mt-6 text-center text-stone-500">
          Loading profile…
        </p>
      ) : notFound ? (
        <p data-testid="profile-not-found" role="alert" className="mt-6 text-center text-stone-600">
          No player found with that name.
        </p>
      ) : query.isError || !query.data ? (
        <p role="alert" className="mt-6 text-center text-red-600">
          Couldn't load this profile — try again shortly.
        </p>
      ) : (
        <ProfileContent profile={query.data} />
      )}
    </section>
  );
}
